import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosPrescription } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

/* ============================================================
   1) CREATE PRESCRIPTION (doctor → patient)
   - consultationId OPTIONNEL (mais supporté)
   ============================================================ */
export const createPrescription = createAsyncThunk(
  "prescription/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { doctorId, patientId, consultationId, ...rest } = payload || {};

      if (!doctorId) throw new Error("doctorId manquant");
      if (!patientId) throw new Error("patientId manquant");

      const url = ENDPOINTS.PRESCRIPTION.CREATE(doctorId);
      const body = {
        patientId,
        ...rest,
        ...(consultationId ? { consultationId } : {}),
      };


      const res = await axiosPrescription.post(url, body);
      return res.data;
    } catch (err) {
      console.error("❌ createPrescription error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la création de l'ordonnance"
      );
    }
  }
);

/* ============================================================
   1b) CLASSIFY + SAVE DOCUMENTS (doctor -> patient)
   - text libre -> classification -> prescriptions/bilans/radios/lettres
   ============================================================ */
export const classifyAndSavePrescriptionDocuments = createAsyncThunk(
  "prescription/classifyAndSave",
  async (payload, { rejectWithValue }) => {
    try {
      const { doctorId, patientId, consultationId, text, ...rest } = payload || {};

      if (!doctorId) throw new Error("doctorId manquant");
      if (!patientId) throw new Error("patientId manquant");
      if (!String(text || "").trim()) throw new Error("Texte medical vide");

      const url = ENDPOINTS.PRESCRIPTION.CLASSIFY_SAVE(doctorId);
      const body = {
        patientId,
        text,
        ...rest,
        ...(consultationId ? { consultationId } : {}),
      };


      const res = await axiosPrescription.post(url, body);
      return res.data;
    } catch (err) {
      console.error("classifyAndSavePrescriptionDocuments error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la classification et sauvegarde"
      );
    }
  }
);

/* ============================================================
   2) FETCH ALL FOR DOCTOR
   ============================================================ */
export const fetchAllPrescriptions = createAsyncThunk(
  "prescription/fetchAll",
  async (doctorId, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");

      const url = ENDPOINTS.PRESCRIPTION.BY_DOCTOR(doctorId);


      const res = await axiosPrescription.get(url);
      return res.data;
    } catch (err) {
      console.error("❌ fetchAllPrescriptions error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la récupération des prescriptions"
      );
    }
  }
);

/* ============================================================
   ✅ FETCH PRESCRIPTIONS FOR A PATIENT (doctor view)
   - /prescriptions/patient/:doctorId/:patientId
   ============================================================ */
export const fetchPrescriptionsByPatientForDoctor = createAsyncThunk(
  "prescription/fetchByPatientForDoctor",
  async ({ doctorId, patientId }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");
      if (!patientId) throw new Error("patientId manquant");

      const url = ENDPOINTS.PRESCRIPTION.BY_PATIENT(doctorId, patientId);


      const res = await axiosPrescription.get(url);
      return res.data;
    } catch (err) {
      console.error("❌ fetchPrescriptionsByPatientForDoctor error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la récupération des prescriptions du patient"
      );
    }
  }
);

/* ============================================================
   ✅ FETCH PRESCRIPTION BY CONSULTATION (doctor view)
   - /prescriptions/consultation/:doctorId/:consultationId
   ============================================================ */
export const fetchPrescriptionByConsultation = createAsyncThunk(
  "prescription/fetchByConsultation",
  async ({ doctorId, consultationId }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");
      if (!consultationId) throw new Error("consultationId manquant");

      const url = ENDPOINTS.PRESCRIPTION.BY_CONSULTATION(
        doctorId,
        consultationId
      );


      const res = await axiosPrescription.get(url);
      return res.data;
    } catch (err) {
      if (err?.response?.status === 404) {
        return [];
      }

      console.error("❌ fetchPrescriptionByConsultation error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la récupération de la prescription de la consultation"
      );
    }
  }
);

/* ============================================================
   4) UPDATE PRESCRIPTION
   ============================================================ */
export const updatePrescription = createAsyncThunk(
  "prescription/update",
  async ({ doctorId, prescriptionId, data }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");
      if (!prescriptionId) throw new Error("prescriptionId manquant");

      const url = ENDPOINTS.PRESCRIPTION.UPDATE(doctorId, prescriptionId);


      const res = await axiosPrescription.patch(url, data);
      return res.data;
    } catch (err) {
      console.error("❌ updatePrescription error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la mise à jour de l’ordonnance"
      );
    }
  }
);

/* ============================================================
   5) DELETE PRESCRIPTION
   ============================================================ */
export const deletePrescription = createAsyncThunk(
  "prescription/delete",
  async ({ doctorId, prescriptionId }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");
      if (!prescriptionId) throw new Error("prescriptionId manquant");

      const url = ENDPOINTS.PRESCRIPTION.DELETE(doctorId, prescriptionId);


      const res = await axiosPrescription.delete(url);

      return { id: prescriptionId, ...res.data };
    } catch (err) {
      console.error("❌ deletePrescription error:", err);
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Erreur lors de la suppression"
      );
    }
  }
);

/* ============================================================
   6) GENERATE PDF
   ============================================================ */
export const generatePrescriptionPdf = createAsyncThunk(
  "prescription/generatePdf",
  async ({ doctorId, prescriptionId }, { rejectWithValue }) => {
    try {
      if (!doctorId) throw new Error("doctorId manquant");
      if (!prescriptionId) throw new Error("prescriptionId manquant");

      const url = ENDPOINTS.PRESCRIPTION.GENERATE_PDF(doctorId, prescriptionId);


      const res = await axiosPrescription.post(url);
      return res.data;
    } catch (err) {
      console.error("❌ generatePrescriptionPdf error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la génération du PDF"
      );
    }
  }
);

/* ============================================================
   7) FETCH PRESCRIPTIONS FOR PATIENT SELF (patient view)
   ============================================================ */
export const fetchPatientPrescriptions = createAsyncThunk(
  "prescription/fetchByPatient",
  async ({ doctorId, patientId }, { rejectWithValue }) => {
    try {
      if (!patientId) throw new Error("patientId manquant");

      let url;

      if (doctorId) {
        // 👨‍⚕️ Vue médecin (doctorId obligatoire)
        url = ENDPOINTS.PRESCRIPTION.BY_PATIENT(doctorId, patientId);
      } else {
        // 👤 Vue patient (self)
        url = ENDPOINTS.PRESCRIPTION.PATIENT_SELF(patientId);
      }


      const res = await axiosPrescription.get(url);
      
      // ✅ IMPORTANT: Vérifier la structure de la réponse
      
      // ✅ FIX: L'API retourne directement un array, pas { data: [...] }
      return res.data; // Devrait être directement l'array
    } catch (err) {
      console.error("❌ fetchPatientPrescriptions error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Erreur lors de la récupération des prescriptions du patient"
      );
    }
  }
);
